import Client from "fhir-kit-client";
import i18n from "i18next";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Badge, Button, Card, Col, Row, Table } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import VulcanPage from "../../components/VulcanPage/VulcanPage";
import {
  DeviceKind,
  FhirDevice,
  formatCodeableConcept,
  getAvailability,
  getCategoryDisplay,
  getContactDisplay,
  getDeviceName,
  getDeviceTypes,
  getDeviceVersion,
  getIdentifierDisplay,
  getReferenceDisplay,
} from "../../services/DeviceAdapter";
import "../DeviceList/DevicePages.css";

type Props = { kind: DeviceKind };
type Detail = { label: string; value?: string };

const DetailList: FunctionComponent<{ title: string; details: Detail[] }> = ({
  title,
  details,
}) => {
  const visible = details.filter((detail) => detail.value);
  if (visible.length === 0) return null;
  return (
    <Card className="card device-detail-card mb-3">
      <Card.Header>
        <h2 className="h5 mb-0">{title}</h2>
      </Card.Header>
      <Card.Body>
        <Table borderless size="sm" className="mb-0">
          <tbody>
            {visible.map((detail) => (
              <tr key={detail.label}>
                <th scope="row">{detail.label}</th>
                <td>{detail.value}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

const DeviceViewer: FunctionComponent<Props> = ({ kind }) => {
  const { deviceId, softwareId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [device, setDevice] = useState<FhirDevice>();
  const fhirClient = useMemo(
    () => new Client({ baseUrl: process.env.REACT_APP_FHIR_URL ?? "fhir" }),
    [],
  );

  const loadDevice = useCallback(async () => {
    setLoading(true);
    try {
      const resource: any = await fhirClient.read({
        resourceType: "Device",
        id: deviceId ?? softwareId ?? "",
      });
      if (resource?.resourceType !== "Device")
        throw new Error("The requested resource is not a Device.");
      setDevice(resource);
    } catch (error) {
      navigate("/Error", { state: { error } });
    } finally {
      setLoading(false);
    }
  }, [deviceId, fhirClient, navigate, softwareId]);

  useEffect(() => {
    loadDevice();
  }, [loadDevice]);

  const titleKey =
    kind === "software" ? "title.softwaredetails" : "title.materialdetails";
  const backRoute = kind === "software" ? "/SoftwareList" : "/MaterialList";
  const versions = (device?.version ?? [])
    .map(
      (version: any) =>
        `${formatCodeableConcept(version.type) || i18n.t("label.version")}: ${version.value}`,
    )
    .join(" | ");
  const udi = (device?.udiCarrier ?? [])
    .map((item: any) =>
      [item.deviceIdentifier, item.issuer, item.carrierHRF]
        .filter(Boolean)
        .join(" — "),
    )
    .join(" | ");
  const properties = (device?.property ?? [])
    .map((item: any) => {
      const value =
        item.valueString ??
        item.valueBoolean ??
        item.valueInteger ??
        formatCodeableConcept(item.valueCodeableConcept) ??
        (item.valueQuantity ? JSON.stringify(item.valueQuantity) : "");
      return `${formatCodeableConcept(item.type)}: ${value}`;
    })
    .filter(Boolean)
    .join(" | ");

  return (
    <VulcanPage titleKey={titleKey} loading={loading} needsLogin={true}>
      <div className="device-page section">
        {device && (
          <>
            <div className="device-detail-heading">
              <div>
                <h1>{getDeviceName(device)}</h1>
                <p className="text-muted mb-0">Device/{device.id}</p>
              </div>
              <div>
                <Badge
                  bg={device.status === "active" ? "success" : "secondary"}
                >
                  {device.status ?? "unknown"}
                </Badge>
                {getAvailability(device) && (
                  <Badge bg="info" className="ms-2">
                    {getAvailability(device)}
                  </Badge>
                )}
              </div>
            </div>
            <Row>
              <Col lg={6}>
                <DetailList
                  title={i18n.t("devices.sections.identification")}
                  details={[
                    {
                      label: i18n.t("label.identifier"),
                      value: getIdentifierDisplay(device.identifier),
                    },
                    {
                      label: i18n.t("label.category"),
                      value: getCategoryDisplay(device),
                    },
                    {
                      label: i18n.t("label.type"),
                      value: getDeviceTypes(device)
                        .map(formatCodeableConcept)
                        .filter(Boolean)
                        .join(", "),
                    },
                    {
                      label: i18n.t("label.manufacturer"),
                      value: device.manufacturer,
                    },
                    {
                      label: i18n.t("label.modelNumber"),
                      value: device.modelNumber,
                    },
                    {
                      label: i18n.t("label.partNumber"),
                      value: device.partNumber,
                    },
                    {
                      label: i18n.t("label.serialNumber"),
                      value: device.serialNumber,
                    },
                    {
                      label: i18n.t("label.lotNumber"),
                      value: device.lotNumber,
                    },
                    { label: i18n.t("label.udi"), value: udi },
                  ]}
                />
              </Col>
              <Col lg={6}>
                <DetailList
                  title={i18n.t("devices.sections.softwareAndTechnical")}
                  details={[
                    {
                      label: i18n.t("label.version"),
                      value: versions || getDeviceVersion(device),
                    },
                    {
                      label: i18n.t("label.conformsTo"),
                      value: (device.conformsTo ?? [])
                        .map((item: any) =>
                          [
                            formatCodeableConcept(item.specification),
                            item.version,
                          ]
                            .filter(Boolean)
                            .join(" "),
                        )
                        .join(", "),
                    },
                    {
                      label: i18n.t("label.specialization"),
                      value: (device.specialization ?? [])
                        .map((item: any) =>
                          [formatCodeableConcept(item.systemType), item.version]
                            .filter(Boolean)
                            .join(" "),
                        )
                        .join(", "),
                    },
                    {
                      label: i18n.t("label.endpoint"),
                      value: (device.endpoint ?? [])
                        .map(getReferenceDisplay)
                        .join(", "),
                    },
                    { label: i18n.t("label.url"), value: device.url },
                    { label: i18n.t("label.properties"), value: properties },
                  ]}
                />
                <DetailList
                  title={i18n.t("devices.sections.assignment")}
                  details={[
                    {
                      label: i18n.t("label.location"),
                      value: getReferenceDisplay(device.location),
                    },
                    {
                      label: i18n.t("label.owner"),
                      value: getReferenceDisplay(device.owner),
                    },
                    {
                      label: i18n.t("label.patient"),
                      value: getReferenceDisplay(device.patient),
                    },
                    {
                      label: i18n.t("label.parent"),
                      value: getReferenceDisplay(device.parent),
                    },
                    {
                      label: i18n.t("label.contact"),
                      value: getContactDisplay(device.contact),
                    },
                    {
                      label: i18n.t("label.notes"),
                      value: (device.note ?? [])
                        .map((note: any) => note.text)
                        .filter(Boolean)
                        .join(" | "),
                    },
                  ]}
                />
                <DetailList
                  title={i18n.t("devices.sections.lifecycle")}
                  details={[
                    { label: i18n.t("label.status"), value: device.status },
                    {
                      label: i18n.t("label.availability"),
                      value: getAvailability(device),
                    },
                    {
                      label: i18n.t("label.statusReason"),
                      value: (device.statusReason ?? [])
                        .map(formatCodeableConcept)
                        .join(", "),
                    },
                    {
                      label: i18n.t("label.manufactureDate"),
                      value: device.manufactureDate,
                    },
                    {
                      label: i18n.t("label.expirationDate"),
                      value: device.expirationDate,
                    },
                    {
                      label: i18n.t("label.lastUpdated"),
                      value: device.meta?.lastUpdated,
                    },
                  ]}
                />
              </Col>
            </Row>
            <Button
              variant="outline-secondary"
              onClick={() => navigate(backRoute)}
            >
              {i18n.t("button.backToList")}
            </Button>
          </>
        )}
      </div>
    </VulcanPage>
  );
};

export default DeviceViewer;
